import React from 'react';

import FormSection from 'containers/FormSectionContainer';
import FieldSet from 'containers/FieldSetContainer';
import FormField from 'containers/FormField';
import ConditionalSection from 'containers/ConditionalSection';
import TextFieldBox from 'components/TextFieldBox';
import NumberFieldBox from 'components/NumberFieldBox';
import CheckboxGroup from 'components/CheckboxGroup';
import RadioButtonGroup from 'components/RadioButtonGroup';
import SelectField from 'components/SelectField';
import FormProvider from 'routes/Documents/containers/FormProvider';

import { has, fieldIs } from 'lib/conditions';

import CEvents from './CEvents';

const accessOpt = opt => `benefits.opts.${opt}`;

const commonOpts = [
  { value: 'yes', label: 'benefits.opts.yes' },
  { value: 'not', label: 'benefits.opts.not' },
];
const commonOptsGeneral = [
  { value: 'yes', label: 'benefits.opts.yes' },
  { value: 'not', label: 'benefits.opts.no' },
];
const commonLevels = [
  { value: 'low', label: 'benefits.opts.low' },
  { value: 'elevated', label: 'benefits.opts.elevated' },
  { value: 'special', label: 'benefits.opts.special' },
];

const LifestyleForm = props => (
  <FormProvider {...props}>
    <FormSection localeId="benefits.sections.disabilityBeneftAndCare">
      <FieldSet localeId="benefits.labels.disabilityBenefit">
        <FormField
          name="disabilityBenefit"
          component={RadioButtonGroup}
          options={commonOpts}
          disabled={fieldIs('careBenefit', 'yes')}
        />
      </FieldSet>
      <ConditionalSection condition={fieldIs('disabilityBenefit', 'yes')}>
        <FieldSet localeId="benefits.labels.disabilityBenefitLevel">
          <FormField
            name="disabilityBenefitLevel"
            component={SelectField}
            hintLocale="benefits.hints.level"
            options={commonLevels}
          />
        </FieldSet>
      </ConditionalSection>
      <FieldSet localeId="benefits.labels.careBenefit">
        <FormField
          name="careBenefit"
          component={RadioButtonGroup}
          options={commonOpts}
          disabled={fieldIs('disabilityBenefit', 'yes')}
        />
      </FieldSet>
      <ConditionalSection condition={fieldIs('careBenefit', 'yes')}>
        <FieldSet localeId="benefits.labels.careBenefitLevel">
          <FormField
            name="careBenefitLevel"
            component={SelectField}
            hintLocale="benefits.hints.level"
            options={commonLevels}
          />
        </FieldSet>
      </ConditionalSection>
    </FormSection>

    <FormSection localeId="benefits.sections.homeCareCareTakingAndPersonalAssistance">
      <FieldSet localeId="benefits.labels.homecare">
        <FormField name="homecare" component={RadioButtonGroup} options={commonOpts} />
      </FieldSet>
      <FieldSet localeId="benefits.labels.caretaker">
        <FormField name="caretaker" component={RadioButtonGroup} options={commonOptsGeneral} />
      </FieldSet>

      <FieldSet localeId="benefits.labels.personalAssistant">
        <FormField name="personalAssistant" component={RadioButtonGroup} options={commonOptsGeneral} />
      </FieldSet>
      <ConditionalSection condition={fieldIs('personalAssistant', 'yes')}>
        <FieldSet localeId="benefits.labels.personalAssistantHours">
          <FormField
            name="personalAssistantHours"
            component={NumberFieldBox}
            hintLocale="benefits.hints.personalAssistantHours"
            min="0"
          />
        </FieldSet>
      </ConditionalSection>

      <FieldSet localeId="benefits.labels.transportation">
        <FormField name="transportation" component={RadioButtonGroup} options={commonOptsGeneral} />
      </FieldSet>

      <FieldSet localeId="benefits.labels.personalAssistance">
        <FormField name="personalAssistance" component={RadioButtonGroup} options={commonOpts} />
      </FieldSet>
      <ConditionalSection condition={fieldIs('personalAssistance', 'yes')}>
        <FieldSet localeId="benefits.labels.personalAssistanceDetail">
          <FormField
            name="personalAssistanceDetail"
            component={CheckboxGroup}
            optionFormatter={accessOpt}
            options={[
              { value: 'movement' },
              { value: 'dressing' },
              { value: 'personalHygienePractices' },
              { value: 'clothingAndFoodServices' },
              { value: 'utility' },
              { value: 'aVisitToTheShop' },
              { value: 'doctorVisits' },
              { value: 'daycare' },
              { value: 'other' },
            ]}
          />
        </FieldSet>
        <ConditionalSection condition={has('personalAssistanceDetail', 'other')}>
          <FieldSet localeId="benefits.labels.personalAssistanceDetailOther">
            <FormField
              name="personalAssistanceDetailOther"
              component={TextFieldBox}
              hintLocale="dictionary.otherWhat"
            />
          </FieldSet>
        </ConditionalSection>
      </ConditionalSection>
    </FormSection>

    <FormSection localeId="benefits.sections.utilitiesAndRenovation">
      <FieldSet localeId="benefits.labels.utilities">
        <FormField name="utilities" component={RadioButtonGroup} preset="yesno" />
      </FieldSet>
      <ConditionalSection condition={fieldIs('utilities', 'yes')}>
        <FieldSet localeId="benefits.labels.utilitiesDetail">
          <FormField
            name="utilitiesDetail"
            component={CheckboxGroup}
            optionFormatter={accessOpt}
            options={[
              { value: 'smallAids' },
              { value: 'officeChair' },
              { value: 'walkingStick' },
              { value: 'orthoses' },
              { value: 'rollator' },
              { value: 'wheelchair' },
              { value: 'electricWheelchair' },
              { value: 'electricMoped' },
              { value: 'communicationTools' },
              { value: 'showerChair' },
              { value: 'toiletAids' },
              { value: 'incontinenceProtection' },
              { value: 'caters' },
              { value: 'safeBowelFunction' },
              { value: 'other' },
            ]}
          />
        </FieldSet>
        <ConditionalSection condition={has('utilitiesDetail', 'other')}>
          <FieldSet localeId="benefits.labels.utilitiesDetailOther">
            <FormField name="utilitiesDetailOther" component={TextFieldBox} hintLocale="dictionary.otherWhat" />
          </FieldSet>
        </ConditionalSection>
      </ConditionalSection>

      <FieldSet localeId="benefits.labels.renovation">
        <FormField name="renovation" component={RadioButtonGroup} options={commonOptsGeneral} />
      </FieldSet>

      <ConditionalSection condition={fieldIs('renovation', 'yes')}>
        <FieldSet localeId="benefits.labels.renovationDetail">
          <FormField
            name="renovationDetail"
            component={CheckboxGroup}
            optionFormatter={accessOpt}
            options={[
              { value: 'wideningOfDoors' },
              { value: 'constructionOfRamps' },
              { value: 'kitchenAlterations' },
              { value: 'lightingModificationsAndTheAdditionOfContrasts' },
              { value: 'installingALift' },
              { value: 'other' },
            ]}
          />
        </FieldSet>
        <ConditionalSection condition={has('renovationDetail', 'other')}>
          <FieldSet localeId="benefits.labels.renovationDetailOther">
            <FormField name="renovationDetailOther" component={TextFieldBox} hintLocale="dictionary.otherWhat" />
          </FieldSet>
        </ConditionalSection>
      </ConditionalSection>
    </FormSection>
    <FormSection localeId="benefits.labels.statements">
      <FormField name="statements" component={CEvents} />
    </FormSection>
  </FormProvider>
);

export default LifestyleForm;
